// components/FieldEditorModal.tsx
import React, { useEffect, useState } from 'react';
import {
  Modal,
  Stack,
  TextInput,
  Select,
  NumberInput,
  Switch,
  Group,
  Button,
  Alert,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { BuilderField, FieldTypeCode, FieldTypeDef, OPTION_TYPES } from '../types/BuilderField';
import { validateField, nextOrderNoForCategory } from '../utils/validation';
import { keyFromLabel, generateFieldId } from '../utils/optionParser';
import { OptionEditor } from './OptionEditor';

const CATEGORY_STORAGE_KEY = 'form-builder:known-categories';

function readStoredCategories(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(CATEGORY_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((c) => typeof c === 'string' && c.trim().length > 0) : [];
  } catch {
    return [];
  }
}

function writeStoredCategories(categories: string[]) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CATEGORY_STORAGE_KEY, JSON.stringify(categories));
  } catch {
    // localStorage unavailable (private mode, quota, etc) — fail silently
  }
}

function addStoredCategory(category: string): string[] {
  const trimmed = category.trim();
  if (!trimmed) return readStoredCategories();
  const next = Array.from(new Set([...readStoredCategories(), trimmed]));
  writeStoredCategories(next);
  return next;
}

/** Clears every category the user has ever typed. Call this on form submission. */
export function clearStoredCategories() {
  writeStoredCategories([]);
}

export interface FieldEditorModalProps {
  opened: boolean;
  onClose: () => void;
  onSave: (field: BuilderField) => void;
  field: BuilderField | null; // null = creating new
  defaultType?: FieldTypeCode;
  categories: string[];
  existingFields: BuilderField[];
  /** Optional — called when the user confirms a brand-new category via "+ Add". */
  onCreateCategory?: (category: string) => void;
  /**
   * Bump this (e.g. Date.now(), or an incrementing counter) whenever the
   * parent form is submitted/published. Clears localStorage-backed
   * categories, so the next form starts with a clean category list.
   */
  resetToken?: number | string;
  /** Pass down the unified dynamic SDK + fallback types */
  availableTypes: FieldTypeDef[];
}

function blankField(
  defaultType: FieldTypeCode | undefined,
  defaultCategory: string,
  existingFields: BuilderField[]
): BuilderField {
  return {
    id: generateFieldId(),
    fieldKey: '',
    label: '',
    fieldType: defaultType ?? 'text',
    category: defaultCategory,
    orderNo: nextOrderNoForCategory(existingFields, defaultCategory),
    required: false,
    active: true,
    placeholder: '',
    options: [],
  };
}

export function FieldEditorModal({
  opened,
  onClose,
  onSave,
  field,
  defaultType,
  categories,
  existingFields,
  onCreateCategory,
  resetToken,
  availableTypes,
}: FieldEditorModalProps) {
  // Remembers the last category used for a *new* field, so the next new
  // field defaults to it instead of resetting back to the first category.
  const [lastUsedCategory, setLastUsedCategory] = useState<string | null>(null);

  const [draft, setDraft] = useState<BuilderField>(() =>
    field ?? blankField(defaultType, lastUsedCategory ?? '', existingFields)
  );
  const [errors, setErrors] = useState<string[]>([]);
  const [keyManuallyEdited, setKeyManuallyEdited] = useState(!!field);

  // Free-typed text in the category field, tracked separately from the
  // committed draft.category so we don't overwrite the real value on every
  // keystroke — only once the user commits it (blur / Enter / pick / Add).
  const [categoryQuery, setCategoryQuery] = useState('');

  // categoryOptions is now backed by localStorage, not just this component's
  // state, so previously typed categories persist across modal opens,
  // reloads, and different fields/sessions.
  const [categoryOptions, setCategoryOptions] = useState<string[]>(() =>
    Array.from(new Set([...readStoredCategories(), ...categories.filter(Boolean)]))
  );

  useEffect(() => {
    if (opened) {
      const initialCategory = lastUsedCategory ?? '';
      setDraft(field ?? blankField(defaultType, initialCategory, existingFields));
      setErrors([]);
      setKeyManuallyEdited(!!field);
      setCategoryQuery('');
      // Re-sync from localStorage every time the modal opens, in case
      // another tab/session added categories since we last read them.
      setCategoryOptions((prev) =>
        Array.from(new Set([...prev, ...readStoredCategories(), ...categories.filter(Boolean)]))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, field]);

  useEffect(() => {
    setCategoryOptions((prev) => Array.from(new Set([...prev, ...categories.filter(Boolean)])));
  }, [categories]);

  // Clears categories (both remembered-for-session and localStorage) when
  // the parent form is submitted, so the next form starts fresh.
  useEffect(() => {
    if (resetToken === undefined) return;
    setLastUsedCategory(null);
    clearStoredCategories();
    setCategoryOptions(categories.filter(Boolean));
    setCategoryQuery('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetToken]);

  const normalizedCategories = categoryOptions.map((c) => c.trim());
  const trimmedQuery = categoryQuery.trim();
  const queryMatchesExisting = normalizedCategories.some(
    (c) => c.toLowerCase() === trimmedQuery.toLowerCase()
  );
  const showAddCategory = trimmedQuery.length > 0 && !queryMatchesExisting;

  /** Commits whatever text is currently typed as the field's category. */
  const commitCategory = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return;

    const stored = addStoredCategory(trimmed);
    setCategoryOptions((prev) => Array.from(new Set([...prev, ...stored])));
    setDraft((d) => ({ ...d, category: trimmed }));
    onCreateCategory?.(trimmed);
    setCategoryQuery('');
  };

  const confirmNewCategory = () => commitCategory(categoryQuery);

  const needsOptions = OPTION_TYPES.includes(draft.fieldType);

  const handleLabelChange = (label: string) => {
    setDraft((d) => ({
      ...d,
      label,
      fieldKey: keyManuallyEdited ? d.fieldKey : keyFromLabel(label),
    }));
  };

  const handleSave = () => {
    // If the user typed a category but never blurred/hit Enter/clicked Add,
    // commit it now so it isn't silently lost.
    if (categoryQuery.trim() && !draft.category?.trim()) {
      commitCategory(categoryQuery);
    }

    const finalDraft = { ...draft, category: (draft.category || categoryQuery).trim() };

    const validationErrors = validateField(finalDraft, {
      existingFields,
      editingFieldId: field?.id,
    });
    if (validationErrors.length > 0) {
      setErrors(validationErrors.map((e) => e.message));
      return;
    }

    const stored = addStoredCategory(finalDraft.category);
    setCategoryOptions((prev) => Array.from(new Set([...prev, ...stored])));
    setLastUsedCategory(finalDraft.category);

    onSave(finalDraft);
  };

  const handleClose = () => {
    if (field) {
      setLastUsedCategory(null);
    }
    onClose();
  };

  return (
    <Modal opened={opened} onClose={handleClose} title={field ? 'Edit Field' : 'New Field'} size="lg">
      <Stack gap="md">
        {errors.length > 0 && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            <Stack gap={4}>
              {errors.map((e, i) => (
                <div key={i}>{e}</div>
              ))}
            </Stack>
          </Alert>
        )}

        <Select
          label="Field Type"
          data={availableTypes.map((t) => ({ value: t.code, label: t.name }))}
          value={draft.fieldType}
          onChange={(v) => setDraft((d) => ({ ...d, fieldType: (v as FieldTypeCode) ?? d.fieldType }))}
        />

        <TextInput 
          label="Label" 
          value={draft.label} 
          onChange={(e) => handleLabelChange(e.currentTarget.value)} 
        />

        <TextInput
          label="Field Key"
          description="Must be unique. Auto-generated from the label unless edited manually."
          value={draft.fieldKey}
          onChange={(e) => {
            const val = e.currentTarget.value; // Clean value read synchronously
            setKeyManuallyEdited(true);
            setDraft((d) => ({ ...d, fieldKey: val }));
          }}
        />

        <Group align="flex-end" wrap="nowrap">
          <Select
            label="Category"
            placeholder="Type to search or add"
            style={{ flex: 1 }}
            data={Array.from(new Set([...categoryOptions, draft.category].filter((c) => c && c.trim().length > 0))).map((c) => ({ value: c, label: c }))}
            value={draft.category || null}
            searchable
            searchValue={categoryQuery}
            nothingFoundMessage={
              trimmedQuery ? `No match — press Enter or click "Add" to create "${trimmedQuery}"` : 'Type to search'
            }
            onSearchChange={setCategoryQuery}
            onChange={(v) => {
              if (!v) return;
              setDraft((d) => ({ ...d, category: v }));
              setCategoryQuery('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && trimmedQuery && !queryMatchesExisting) {
                e.preventDefault();
                commitCategory(categoryQuery);
              }
            }}
            onBlur={() => {
              if (trimmedQuery) {
                commitCategory(categoryQuery);
              }
            }}
            onDropdownClose={() => {
              if (trimmedQuery) {
                commitCategory(categoryQuery);
              }
            }}
          />
          {showAddCategory && (
            <Button variant="light" onClick={confirmNewCategory}>
              Add
            </Button>
          )}
          <NumberInput
            label="Order"
            min={1}
            style={{ width: 120, flexShrink: 0 }}
            value={draft.orderNo}
            onChange={(v) => setDraft((d) => ({ ...d, orderNo: typeof v === 'number' ? v : 1 }))}
          />
        </Group>

        <TextInput
          label="Placeholder"
          value={draft.placeholder ?? ''}
          onChange={(e) => {
            const nextValue = e.currentTarget.value; // Clean value read synchronously
            setDraft((d) => ({ ...d, placeholder: nextValue }));
          }}
        />

        <Group>
          <Switch
            label="Required"
            checked={draft.required}
            onChange={(e) => {
              const nextChecked = e.currentTarget.checked; // Clean value read synchronously
              setDraft((d) => ({ ...d, required: nextChecked }));
            }}
          />
          <Switch
            label="Active"
            checked={draft.active}
            onChange={(e) => {
              const nextChecked = e.currentTarget.checked; // Clean value read synchronously
              setDraft((d) => ({ ...d, active: nextChecked }));
            }}
          />
        </Group>

        {needsOptions && (
          <OptionEditor
            options={draft.options ?? []}
            onChange={(options) => setDraft((d) => ({ ...d, options }))}
          />
        )}

        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave}>Save</Button>
        </Group>
      </Stack>
    </Modal>
  );
}